import { type ReactNode, type ElementType } from "react";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  /** Page title — rendered as an <h1> */
  title: string;
  /** Short description shown below the title */
  subtitle?: string;
  /** Optional Lucide icon constructor */
  icon?: ElementType;
  /** Tailwind text-color class for the icon, e.g. "text-violet-600" */
  iconColor?: string;
  /** Tailwind bg class for the icon badge, e.g. "bg-violet-500/10" */
  iconBg?: string;
  /** Action buttons / controls placed on the right */
  action?: ReactNode;
  className?: string;
}

/**
 * Consistent page-level header used across all employee-facing pages.
 *
 * Usage:
 *   <PageHeader
 *     title="Time Off"
 *     subtitle="Manage your leave requests and balances"
 *     icon={CalendarDays}
 *     iconColor="text-blue-600"
 *     iconBg="bg-blue-500/10"
 *     action={<Button>Request leave</Button>}
 *   />
 */
export function PageHeader({
  title,
  subtitle,
  icon: Icon,
  iconColor = "text-primary",
  iconBg = "bg-primary/10",
  action,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn("flex items-center justify-between flex-wrap gap-3", className)}>
      <div className="flex items-center gap-3.5">
        {Icon && (
          <div
            className={cn(
              "h-11 w-11 rounded-xl flex items-center justify-center shrink-0 border border-border/40",
              iconBg,
            )}
          >
            <Icon className={cn("h-5 w-5", iconColor)} />
          </div>
        )}
        <div>
          <h1 className="text-2xl font-bold text-foreground leading-tight">{title}</h1>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>

      {action && <div className="flex items-center gap-2">{action}</div>}
    </div>
  );
}
